import { BaseMethodReturnType } from 'src/types/commonTypes';
import {
  ConnectionInfoStrategy,
  IConnectionInfoSubsystemStrategy
} from './types';
import BrowserConnectionInfoStrategy from './BrowserConnectionInfoStrategy';
import JWebConnectionInfoStrategy from './JWebConnectionInfoStrategy';

class ConnectionInfoSubsystemStrategy
  implements IConnectionInfoSubsystemStrategy
{
  private _strategy: Promise<IConnectionInfoSubsystemStrategy>;

  private async _getStrategy(): Promise<IConnectionInfoSubsystemStrategy> {
    const manifest =
      (window as any).Shell?.manifest ||
      (window as any).clientOSWebMFE?.manifest;
    const options = manifest?.northboundAPIs?.connectionInfo || {};

    let strategy: IConnectionInfoSubsystemStrategy;
    switch (options?.strategy) {
      case ConnectionInfoStrategy.BROWSER:
        strategy = new BrowserConnectionInfoStrategy();
        break;
      case ConnectionInfoStrategy.EVENT:
        strategy = new JWebConnectionInfoStrategy();
        break;
      default: {
        const isValidJWebStrategy = await this._isValidJWebStrategy();
        if (isValidJWebStrategy) {
          strategy = new JWebConnectionInfoStrategy();
        } else {
          strategy = new BrowserConnectionInfoStrategy();
        }
        break;
      }
    }
    this._strategy = new Promise((resolve) => resolve(strategy));

    return this._strategy;
  }

  private async _isValidJWebStrategy(): Promise<boolean> {
    try {
      const jweb = (window as any)?.JWeb;
      const isNative = jweb?.isNative;
      if (!isNative) {
        return false;
      }
      const { Plugins } = jweb || {};
      const hasEventServicePlugin =
        Plugins?.EventService && Object.keys(Plugins.EventService).length > 0;
      const hasDevicePlugin =
        Plugins?.Device && Object.keys(Plugins.Device).length > 0;
      const getInfoResult = await jweb?.Device?.getInfo();

      return (
        hasDevicePlugin &&
        typeof getInfoResult?.isInternetConnected !== 'undefined' &&
        hasEventServicePlugin
      );
    } catch (ex) {
      console.warn(
        `[ConnectionInfoNorthboundAPI]: Error while checking JWeb strategy: ${ex}`
      );
      return false;
    }
  }

  async hasInternetAccess(): Promise<BaseMethodReturnType<boolean>> {
    const strategy = await this._getStrategy();

    return strategy.hasInternetAccess();
  }

  async listenHasInternetAccess(
    callback: (hasInternetAccess: boolean) => void
  ): Promise<BaseMethodReturnType<{ remove: () => void }>> {
    const strategy = await this._getStrategy();

    return strategy.listenHasInternetAccess(callback);
  }
}

const connectionInfoSubsystemStrategy = new ConnectionInfoSubsystemStrategy();

export default connectionInfoSubsystemStrategy;
